import { createFileRoute, Link } from "@tanstack/react-router";

const LAST_UPDATED = "July 27, 2026";

const GOOGLE_DATA_USES = [
  {
    service: "Google Sign-In (email, profile)",
    purpose:
      "Authenticate you and link your FlowState account to your Google identity.",
  },
  {
    service: "Google Calendar",
    purpose:
      "Read and write calendar events for scheduling, bookings, meeting links, and calendar sync.",
  },
  {
    service: "Gmail",
    purpose:
      "Read and send messages within the scopes you grant so automations and Cortex can assist with client communication.",
  },
  {
    service: "Google Drive and Docs",
    purpose:
      "Create and update documents you authorise, and store backups in folders you choose.",
  },
  {
    service: "Google Tasks",
    purpose: "Sync tasks between FlowState and Google Tasks when enabled.",
  },
  {
    service: "Gemini / Generative Language",
    purpose:
      "Run AI features using your own Google AI credentials when you select Google as your model provider.",
  },
];

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      {
        title: "Privacy Policy — FlowState Automate",
      },
      {
        name: "description",
        content:
          "How FlowState Automate collects, uses, and protects your information, including Google user data handled under the Google API Services User Data Policy.",
      },
    ],
  }),
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-primary hover:text-primary/80 transition-colors mb-4"
          >
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="prose prose-gray dark:prose-invert max-w-none space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <p className="text-foreground leading-relaxed">
              This Privacy Policy explains how FlowState Automate Pty Ltd
              (&quot;FlowState&quot;, &quot;we&quot;, &quot;us&quot;) collects,
              uses, and discloses information when you use the FlowState
              Automate application and related services (the
              &quot;Service&quot;).
            </p>
            <p className="text-foreground leading-relaxed mt-4">
              By using the Service you agree to the collection and use of
              information in accordance with this policy. See also our{" "}
              <Link
                to="/terms"
                className="text-primary hover:text-primary/80 underline"
              >
                Terms of Service
              </Link>
              .
            </p>
          </div>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              1. Information We Collect
            </h2>
            <p className="text-foreground leading-relaxed mb-3">
              We collect information you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-foreground">
              <li>Name and contact information (email, phone number)</li>
              <li>Account credentials and organisation membership data</li>
              <li>
                Business records you create in the Service, such as clients,
                projects, tasks, invoices, and documents
              </li>
              <li>Support requests and other information you choose to provide</li>
            </ul>
            <p className="text-foreground leading-relaxed mt-4">
              We also collect usage data automatically, including IP address,
              browser type and version, pages visited, timestamps, and
              diagnostic data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              2. How We Use Your Information
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-foreground">
              <li>Provide, maintain, and improve the Service</li>
              <li>
                Authenticate users and operate organisation-scoped workspaces
                with role-based access
              </li>
              <li>
                Sync calendars, email, tasks, and documents when you enable
                integrations
              </li>
              <li>Power Cortex and other AI features with credentials you supply</li>
              <li>Respond to your requests and send service communications</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              3. Google User Data
            </h2>
            <p className="text-foreground leading-relaxed mb-4">
              Connecting a Google account is optional. When you connect Google,
              we request only the scopes needed for the integrations you enable,
              and we use the resulting data for the purposes below.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-2 pr-4 font-semibold text-foreground">
                      Google service
                    </th>
                    <th className="py-2 font-semibold text-foreground">
                      Why we request access
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {GOOGLE_DATA_USES.map((row) => (
                    <tr key={row.service} className="border-b border-border align-top">
                      <td className="py-3 pr-4 font-medium text-foreground">
                        {row.service}
                      </td>
                      <td className="py-3 text-muted-foreground leading-relaxed">
                        {row.purpose}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3 className="text-xl font-medium text-foreground mt-6 mb-3">
              Limited Use
            </h3>
            <p className="text-foreground leading-relaxed mb-3">
              FlowState Automate&apos;s use and transfer of information received
              from Google APIs to any other app adheres to the{" "}
              <a
                href="https://developers.google.com/terms/api-services-user-data-policy"
                className="text-primary hover:text-primary/80 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google API Services User Data Policy
              </a>
              , including the Limited Use requirements. Specifically, we use
              Google user data only to provide or improve user-facing features
              that are prominent in the Service. We do not sell Google user
              data, we do not use it for advertising, and we do not use it to
              train generalized AI or machine-learning models.
            </p>
            <p className="text-foreground leading-relaxed">
              You can disconnect Google integrations at any time in the
              application settings. Revoking access from your{" "}
              <a
                href="https://myaccount.google.com/permissions"
                className="text-primary hover:text-primary/80 underline"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Account permissions
              </a>{" "}
              also stops further API access.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              4. Information Sharing
            </h2>
            <p className="text-foreground leading-relaxed">
              We do not sell your personal information. We share information
              with service providers who help us operate the Service — such as
              hosting, database, email delivery, and AI model providers — under
              confidentiality obligations and only as needed to deliver the
              features you use. We may also disclose information where required
              by law or to protect our rights and the safety of users.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              5. Data Retention
            </h2>
            <p className="text-foreground leading-relaxed">
              We retain personal data for as long as your account is active or
              as needed to provide the Service, comply with legal obligations,
              resolve disputes, and enforce our agreements. Usage data is
              generally retained for shorter periods except where needed for
              security or service integrity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              6. Data Security
            </h2>
            <p className="text-foreground leading-relaxed">
              We use appropriate technical and organisational measures to
              protect your information, including encryption in transit,
              row-level access controls, and organisation-scoped data isolation.
              No method of transmission or storage is completely secure, so we
              cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              7. Your Rights
            </h2>
            <p className="text-foreground leading-relaxed">
              You may access, correct, export, or delete your personal
              information. Much of this is available directly in your account
              settings; otherwise contact us using the details below. You can
              also request deletion of your account and associated data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              8. Cookies
            </h2>
            <p className="text-foreground leading-relaxed">
              We use cookies and similar technologies for authentication,
              preferences, and analytics. You can configure your browser to
              refuse cookies, but some features may not function properly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-foreground leading-relaxed">
              The Service is not directed to anyone under 16, and we do not
              knowingly collect personal information from children. If you
              believe a child has provided us with personal data, contact us and
              we will remove it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              10. Changes to This Policy
            </h2>
            <p className="text-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will post
              the updated policy on this page and revise the &quot;Last
              updated&quot; date above.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mb-4">
              11. Contact Us
            </h2>
            <p className="text-foreground leading-relaxed">
              FlowState Automate Pty Ltd, 440 Collins Street, Melbourne, VIC
              3000, Australia. Questions about this policy can be sent to{" "}
              <a
                href="mailto:hello@flowstateautomate.ai"
                className="text-primary hover:text-primary/80 underline"
              >
                hello@flowstateautomate.ai
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
